import { useCallback, useMemo } from 'react';

import maxBy from 'lodash/maxBy';

import { SUBSCRIPTION_STATUSES, ARTIST_TYPE_ID } from 'constants/index';

import useAppContext from 'utils/hooks/useAppContext';
import useGetTargetProfile from 'utils/hooks/useGetTargetProfile';
import { waitfor } from 'utils/hooks/useWaitFor';
import { useQuery, useMutation, useSetQueryData, useInvalidateQueries } from 'utils/react-query';

import queries, { fetchUserSubscriptions } from 'containers/Accounts/queries';

import { ENTITY_TYPES } from 'operabase-router/constants';

const EMPTY_ARRAY_READ_ONLY = Object.freeze([]);
const EMPTY_OBJECT_READ_ONLY = Object.freeze({});

const getActiveProfileId = (profiles, savedActiveProfileId) => {
  const allUserProfileIds = profiles?.map(profile => profile.id);

  if (!savedActiveProfileId || !allUserProfileIds.includes(savedActiveProfileId)) {
    return allUserProfileIds?.[0];
  }

  return savedActiveProfileId;
};

const useBaseUserAuthQuery = (queryFn, select) => {
  const { isLoggedIn, userId } = useAppContext();
  const query = queryFn({ id: userId, queryConfig: { enabled: !!(isLoggedIn && userId), select } });

  const { data, isFetching, isSuccess, refetch } = useQuery(query);

  return {
    queryKey: query.queryKey,
    data,
    isFetching,
    isSuccess,
    refetch,
  };
};

const useUserData = () => {
  const { data: userData } = useBaseUserAuthQuery(queries.getUserDetails);

  return userData || EMPTY_OBJECT_READ_ONLY;
};

const useUserDataRefetch = () => {
  const { refetch, isFetching } = useBaseUserAuthQuery(queries.getUserDetails);

  return { refetch, isFetching };
};

const useUserPermissions = () => {
  const { userId } = useAppContext();
  const { data, isFetching, isSuccess, refetch } = useBaseUserAuthQuery(
    queries.getUserPermissions,
    response => response?.data,
  );
  return {
    isLoading: isFetching,
    loadedOnce: (isSuccess && !!data) || !userId,
    permissions: data || EMPTY_ARRAY_READ_ONLY,
    refetch,
  };
};

const useUserPaymentMethods = () => {
  const { data, isFetching, isSuccess, refetch } = useBaseUserAuthQuery(
    queries.getUserPaymentMethods,
    response => response?.data,
  );

  return {
    refetchPaymentMethods: callback => {
      if (!callback) {
        return refetch();
      }

      return refetch().then(({ data: methods }) => {
        const newestMethod = maxBy(methods, met => met.id);

        callback(newestMethod);
      });
    },
    isLoading: isFetching,
    loadedOnce: isSuccess && !!data,
    data: data || EMPTY_ARRAY_READ_ONLY,
  };
};

const useUserDefaultPaymentMethod = () => {
  const { data } = useBaseUserAuthQuery(queries.getUserPaymentMethods, response =>
    response?.data?.find(pm => pm?.is_default),
  );

  return data;
};

const useUserProfiles = () => {
  const { data } = useBaseUserAuthQuery(queries.getUserProfiles, response => response?.data);
  return data || EMPTY_ARRAY_READ_ONLY;
};

const useAccessPendingProfileIds = () => {
  const { data } = useBaseUserAuthQuery(queries.getUserProfiles, response => response?.accessPendingProfileIds);
  return data || EMPTY_ARRAY_READ_ONLY;
};

const useAccessPendingAgencyIds = () => {
  const { data } = useBaseUserAuthQuery(queries.getUserProfiles, response => response?.accessPendingAgencyIds);
  return data || EMPTY_ARRAY_READ_ONLY;
};

const useAccessPendingOrganizationIds = () => {
  const { data } = useBaseUserAuthQuery(queries.getUserProfiles, response => response?.accessPendingOrganizationIds);
  return data || EMPTY_ARRAY_READ_ONLY;
};

const useActiveProfileData = () => EMPTY_OBJECT_READ_ONLY;

const useProfileAccesses = profileId => {
  const { userId } = useAppContext();
  const {
    data: profileAccesses,
    isFetching,
    refetch,
  } = useQuery(
    queries.getProfileAccesses({
      userId,
      profileId,
      queryConfig: { enabled: !!userId },
    }),
  );
  const ownerProfile = profileAccesses?.data?.find(access => access?.isOwner);
  return {
    isLoading: isFetching,
    refetch,
    data: profileAccesses || EMPTY_ARRAY_READ_ONLY,
    ownerProfile: ownerProfile || {},
  };
};

const useEntityTypeGroupedPageAccesses = () => {
  const { data: pageAccesses, ...rest } = useProfileAccesses();

  const isAgencyProfile = useCallback(item => !!item?.agency, []);
  const isOrganizationProfile = useCallback(item => !!item?.organization, []);
  const isArtistProfile = useCallback(
    item => !isOrganizationProfile(item) && !isAgencyProfile(item),
    [isOrganizationProfile, isAgencyProfile],
  );

  const groupedPageAccesses = useMemo(
    () =>
      (pageAccesses?.data || []).reduce((acc, item) => {
        const isAgency = isAgencyProfile(item);
        const isOrganization = isOrganizationProfile(item);
        const isArtist = isArtistProfile(item);

        let entityType = null;
        let entity = null;

        if (isArtist) {
          entityType = ENTITY_TYPES.PROFILE;
          entity = item?.profile;
        } else if (isOrganization) {
          entityType = ENTITY_TYPES.ORGANIZATION;
          entity = item?.organization;
        } else if (isAgency) {
          entityType = ENTITY_TYPES.AGENCY;
          entity = item?.agency;
        }

        if (entityType) {
          if (!acc[entityType]) {
            acc[entityType] = [];
          }

          acc[entityType].push({
            entity,
          });
        }

        return acc;
      }, {}),
    [pageAccesses?.data, isAgencyProfile, isOrganizationProfile, isArtistProfile],
  );

  return {
    ...rest,
    data: groupedPageAccesses,
  };
};

const useTargetProfileSubscription = () => {
  const { profileData: targetProfile, entityType: targetEntityType } = useGetTargetProfile();
  const { data } = useQuery(
    queries.getTargetProfileSubscriptions({
      id: targetProfile?.profile?.id || targetProfile?.id,
      targetEntityType,
      queryConfig: { enabled: !!targetProfile?.id, select: response => response?.data },
    }),
  );

  return data || EMPTY_ARRAY_READ_ONLY;
};

const useUserSubscriptions = () => {
  const { data, isFetching, isSuccess, refetch } = useBaseUserAuthQuery(
    queries.getUserSubscriptions,
    response => response?.data,
  );

  return {
    refetchUserSubscriptions: refetch,
    isLoading: isFetching,
    loadedOnce: isSuccess && !!data,
    data: data || EMPTY_ARRAY_READ_ONLY,
  };
};

const useSetUserData = () => {
  const { language, userId, setLoggedInUserId } = useAppContext();
  const setQueryData = useSetQueryData();

  return payload => {
    const forUserId = payload?.id || userId;
    setQueryData([language, '_GET_USER_DETAILS', `${forUserId}`], payload);

    if (forUserId !== userId) {
      setLoggedInUserId(forUserId);
    }
  };
};

const useUpdateUserData = () => {
  const { userId } = useAppContext();
  const { mutate, ...rest } = useMutation(queries.updateUserDetails());
  const setUserData = useSetUserData();

  return {
    ...rest,
    updateUserData: (payload = {}, options = {}) => {
      const { callback, ...restPayload } = payload;
      const { onSuccess, onSettled } = options;

      mutate(
        {
          endpointParams: { id: userId },
          data: restPayload,
        },
        {
          ...options,
          onSuccess: (data, variables, context) => {
            setUserData(data?.data);

            if (onSuccess) {
              onSuccess(data, variables, context);
            }
          },
          onSettled: (data, error, variables, context) => {
            if (typeof onSettled === 'function') {
              onSettled(data, error, variables, context);
            } else if (typeof callback === 'function') {
              const status = error ? 'error' : 'ok';

              callback({
                status,
                data: data?.data,
                error,
              });
            }
          },
        },
      );
    },
  };
};

const useSetActiveProfileId = () => {
  const { language, userId } = useAppContext();
  const { mutate, ...rest } = useMutation(queries.updateActiveProfileId());
  const invalidateQueries = useInvalidateQueries();

  return {
    ...rest,
    setActiveProfileId: (payload = {}, options = {}) => {
      const { profileId } = payload;
      const { onSuccess } = options;

      mutate(
        {
          endpointParams: { id: userId },
          data: {
            value: profileId,
          },
        },
        {
          ...options,
          onSuccess: (data, variables, context) => {
            invalidateQueries({ queryKey: [language, '_GET_USER_ACTIVE_PROFILE_ID'], exact: false });

            if (onSuccess) {
              onSuccess(data, variables, context);
            }
          },
        },
      );
    },
  };
};

const useSubscriptionChurnedStatus = () => {
  const { profileData, entityType } = useGetTargetProfile();
  const activeProfileSubscriptions = useTargetProfileSubscription();

  const status = useMemo(() => {
    if (entityType === ENTITY_TYPES.PROFILE) {
      return false;
    }

    const cancelledSubscription = activeProfileSubscriptions.find(
      subscription =>
        subscription?.status === SUBSCRIPTION_STATUSES.CANCELLED &&
        subscription?.profile?.profileType?.id === ARTIST_TYPE_ID &&
        subscription?.profile?.id === profileData?.id,
    );
    const validSubscriptions = activeProfileSubscriptions.some(
      subscription => subscription?.status !== SUBSCRIPTION_STATUSES.CANCELLED,
    );

    return !validSubscriptions && cancelledSubscription;
  }, [entityType, activeProfileSubscriptions, profileData?.id]);

  return status;
};

const useWaitForUserSubscriptionUpdate =
  () =>
  (targetSubscription, checkFn, { tries, delay } = {}) =>
    waitfor(
      async () => {
        const subscriptions = await fetchUserSubscriptions({ id: targetSubscription?.user?.id, limit: 100 });
        const matchedSubscription = subscriptions.data.find(sub => sub.id === targetSubscription?.id);

        return checkFn(matchedSubscription);
      },
      tries,
      delay,
    );

const useRefetchUserQueries = () => {
  const invalidateQueries = useInvalidateQueries();

  const QUERY_NAME_MAP = {
    details: ['_GET_USER_DETAILS', '_GET_USER_PROFILES', '_GET_USER_PROFILE_ACCESSES'],
    permissions: ['_GET_USER_PERMISSIONS', '_GET_USER_ACTIVE_PROFILE_PERMISSIONS'],
    subscriptions: ['_GET_USER_SUBSCRIPTIONS', '_GET_USER_TARGET_PROFILE_SUBSCRIPTIONS'],
    payments: ['_GET_USER_PAYMENT_METHODS'],
  };

  return options => {
    const { details = false, permissions = false, subscriptions = false, payments = false } = options || {};
    const keys = [];

    if (details) {
      keys.push(...QUERY_NAME_MAP.details);
    }

    if (permissions) {
      keys.push(...QUERY_NAME_MAP.permissions);
    }

    if (subscriptions) {
      keys.push(...QUERY_NAME_MAP.subscriptions);
    }

    if (payments) {
      keys.push(...QUERY_NAME_MAP.payments);
    }

    if (!keys.length) {
      keys.push(...Object.values(QUERY_NAME_MAP).flat());
    }

    return invalidateQueries({
      predicate: query =>
        // NOTE: At least one of the query key parameter must exist if query supports it.
        keys.includes(query?.queryKey?.[1]) && (query?.queryKey?.length >= 3 ? !!query?.queryKey[2] : true),
    });
  };
};

export {
  getActiveProfileId,
  useUserData,
  useUserPermissions,
  useUserSubscriptions,
  useTargetProfileSubscription,
  useUserPaymentMethods,
  useUserDefaultPaymentMethod,
  useUserProfiles,
  useActiveProfileData,
  useUpdateUserData,
  useSetUserData,
  useSetActiveProfileId,
  useSubscriptionChurnedStatus,
  useWaitForUserSubscriptionUpdate,
  useAccessPendingProfileIds,
  useAccessPendingAgencyIds,
  useAccessPendingOrganizationIds,
  useRefetchUserQueries,
  useProfileAccesses,
  useUserDataRefetch,
  useEntityTypeGroupedPageAccesses,
};
